import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import * as Material from "@mui/material";

import ConfigLoader from "../../components/ConfigLoader";
import ConfigHeaderActions from "../../components/ConfigHeaderActions";
import PlatformCard, { PlatformGrid } from "../../components/PlatformCard";
import useConfigHeader from "../../hooks/useConfigHeader";
import Popper from "@fragments/Popper";
import { Table, Button, Input, Text } from "@fragments";

import "./styles.css";

import api from "@helpers/api";

import CustomModal from "@components/CustomModal";

import {
  IconDotsVertical,
  IconFocus2,
  IconPlus,
  IconSettings,
  IconTrash,
} from "@tabler/icons-react";

import { useCommunity } from "contexts/community";

import { useParams } from "react-router-dom";

import fb from "@assets/logo/partners/facebook.png";
import ga from "@assets/logo/partners/ga.png";
import gtm from "@assets/logo/partners/gtm.svg";
import ginasteeLogo from "@assets/logo/square-small-icon.svg";

const providers = [
  {
    identity: "facebook",
    label: "Facebook Pixel",
    icon: fb,
  },
  {
    identity: "hosted",
    label: "Ginastee",
    icon: ginasteeLogo,
  },
  {
    identity: "google_analytics",
    label: "Google Analytics",
    icon: ga,
  },
  {
    identity: "google_tag_manager",
    label: "Google Tag Manager",
    icon: gtm,
  },
];

export default function Pixels({ info, handleRefresh }) {
  const { t } = useTranslation("settings");
  const [loading, setLoading] = useState(true);

  // Page identity lives in the floating settings header; the install action
  // is portaled into it once the list has loaded.
  useConfigHeader({ title: t("pixel.title"), icon: <IconFocus2 /> });

  let { slug } = useParams();

  const [currentCommunity, setCurrentCommunity] = useState();

  const {
    loading: communityLoading,
    community,
    loadCommunities,
  } = useCommunity();

  const REF_config = useRef();
  const REF_configPixelModal = useRef();

  const popperRef = useRef();

  useEffect(() => {
    if (!communityLoading) handleLoad();
  }, [communityLoading]);

  async function handleLoad() {
    if (info) {
      REF_config.current = { ...info };
      setCurrentCommunity({ ...info });
      setLoading(false);
    }
  }

  function handleConfigurePixel(pixel = null) {
    if (!pixel) return handleInstallPixel();
    REF_configPixelModal.current.open(pixel, async function (result) {
      handleRefresh();
    });
  }

  // New pixel for a provider (the available-platform cards pass theirs).
  function handleInstallPixel(providerId = "facebook") {
    handleConfigurePixel({
      community: info?.id,
      provider: { id: providerId, pixel: "" },
    });
  }

  function handleDeletePixel(id) {
    window.openConfirm({
      title: t("pixel.delete.title"),
      headline: t("shared.warning"),
      subtitle: t("pixel.delete.subtitle"),
      buttons: [
        {
          text: t("shared.deletePermanently"),
          onClick: handleExecuteDelete,
          variant: "contained",
          className: "",
          color: "error",
        },
        {
          text: t("pixel.delete.keep"),
          variant: "text",
        },
      ],
    });

    async function handleExecuteDelete() {
      // api.delete never throws — it returns the axios error object on failure
      const res = await api.delete(`community/pixel/${id}`);
      if (res?.status === 204) {
        window.setSnackbar(t("pixel.delete.success"));
        handleRefresh();
      } else {
        window.setSnackbar(
          t("pixel.delete.error"),
        );
        console.log(res);
      }
    }
  }

  function handleOpenPixelOptions(event, pixel) {
    const options = [
      {
        id: "configure",
        label: t("shared.configure"),
        icon: <IconSettings size={16} />,
        onClick: async () => {
          handleConfigurePixel(pixel);
          popperRef.current.close();
        },
      },
      {
        id: "delete",
        label: t("pixel.delete.menu"),
        icon: <IconTrash size={16} />,
        variant: "danger",
        onClick: async () => {
          handleDeletePixel(pixel.id);
          popperRef.current.close();
        },
      },
    ];

    popperRef.current.open(options, "bottom", event.currentTarget);
  }

  const columns = [
    {
      title: t("pixel.columns.status"),
      width: "60px",
      align: "center",
      customRender: (item) => item.active && <div className="live-indicator" />,
    },
    {
      title: t("pixel.columns.provider"),
      width: "250px",
      customRender: (item) => {
        const config = getProvider(item.provider.id);
        return (
          <div className="plan-config-row">
            <img
              src={config.icon}
              className="pixel-icon"
              style={{ width: 22, height: 22, marginRight: 8 }}
            />
            <Text.span contrast={6}>{config.label}</Text.span>
          </div>
        );
      },
    },
    {
      title: t("pixel.columns.identifier"),
      grow: 1,
      customRender: (item) => (
        <Text.span contrast={6}>{item.provider.pixel}</Text.span>
      ),
    },
  ];

  function CreatePixelButton() {
    return (
      <Button
        onClick={() => handleConfigurePixel()}
        endIcon={<IconPlus />}
        text={t("pixel.install")}
        size="small"
      />
    );
  }

  return (
    <div
      className="config-page config-general-community config-plans-page"
      key={loading}
    >
      {!(loading || communityLoading) && (
        <ConfigHeaderActions>
          <CreatePixelButton />
        </ConfigHeaderActions>
      )}

      {loading || communityLoading ? (
        <ConfigLoader />
      ) : (
        <div className="mt-4" key={Math.random()}>
          <div className="px-3">
            {/* Available platforms — "+" opens the install modal with the
                provider preselected. Ginastee's own pixel is not installable. */}
            <Text.h5 weight={500} contrast={9} className="mb-2">
              {t("pixel.available.title")}
            </Text.h5>
            <PlatformGrid>
              {providers
                .filter((p) => p.identity !== "hosted")
                .map((p) => (
                  <PlatformCard
                    key={p.identity}
                    logo={p.icon}
                    name={p.label}
                    description={t(`pixel.providers.${p.identity}.description`)}
                    addLabel={t("pixel.install")}
                    onAdd={() => handleInstallPixel(p.identity)}
                  />
                ))}
            </PlatformGrid>

            <Text.h5 weight={500} contrast={9} className="mt-4 mb-2">
              {t("pixel.installed.title")}
            </Text.h5>
            <Table
              id="community-pixels-table"
              columns={columns}
              contained={true}
              data={currentCommunity?.pixels || []}
              loading={loading}
              actionsComponent={(item) => {
                const disabledEdition = item.provider.id === "hosted";
                if (disabledEdition) return null;
                return (
                  <div
                    className="plan-config-row"
                    style={{
                      gap: "8px",
                      justifyContent: "flex-end",
                      width: "100%",
                    }}
                  >
                    <span
                      className="plan-row-btn"
                      onClick={() => handleConfigurePixel(item)}
                      title={t("shared.configure")}
                    >
                      <IconSettings size={16} />
                    </span>
                    <span
                      className="plan-row-btn"
                      onMouseEnter={(event) =>
                        handleOpenPixelOptions(event, item)
                      }
                      onClick={(event) => handleOpenPixelOptions(event, item)}
                      title={t("shared.moreOptions")}
                    >
                      <IconDotsVertical size={16} />
                    </span>
                  </div>
                );
              }}
            />
          </div>
        </div>
      )}

      <Popper controlRef={popperRef} config={{ activateOnHover: true }} />

      <PixelConfigModal modalRef={REF_configPixelModal} />
    </div>
  );
}

function getProvider(code) {
  return providers.find((p) => p.identity === code);
}

function PixelConfigModal({ modalRef }) {
  const { t } = useTranslation("settings");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const [error, setError] = useState();

  const [provider, setProvider] = useState("facebook");
  const [id, setId] = useState("");

  const REF_config = useRef({});
  const REF_callback = useRef();

  modalRef.current = {
    open(pixel, callback = () => {}) {
      REF_config.current = { ...pixel };
      REF_callback.current = callback;
      console.log("Config pixel", pixel);
      setError(null);
      setId(pixel?.provider?.pixel || "");
      setProvider(pixel?.provider?.id || "facebook");
      setOpen(true);
    },
  };

  function handleChangeProvider(value) {
    setProvider(value);
  }
  async function handleSubmit() {
    try {
      const valid = isValid();

      if (!valid) {
        setError(t("pixel.modal.invalidId"));
        return;
      } else {
        setError(null);
      }

      setLoading(true);

      const { data } = await api.post("community/pixel", {
        community: REF_config.current.community,
        provider: {
          id: provider,
          pixel: id,
        },
      });

      if (!data.id) {
        setError(t("pixel.modal.saveError"));
      } else {
        REF_callback.current(data);
        setOpen(false);
      }
    } catch (e) {
      setError(t("pixel.modal.unexpectedError"));
      console.log(e);
    }

    setLoading(false);

    function isValid() {
      if (typeof id !== "string" || id.trim() === "") return false;

      switch (provider) {
        case "facebook":
          return /^[0-9]{5,20}$/.test(id); // Facebook Pixel ID: geralmente número
        case "google_analytics":
          return /^(UA-\d{4,9}-\d{1,4}|G-\w+)$/.test(id); // GA ID: tipo UA-XXXXX-Y ou G-XXXX
        case "google_tag_manager":
          return /^GTM-[A-Z0-9]+$/.test(id); // GTM ID: tipo GTM-XXXX
        default:
          return false;
      }
    }
  }

  return (
    <>
      <CustomModal
        maxWidth={360}
        bottomButtonCancelText={t("common:actions.cancel")}
        loading={loading}
        title={t("pixel.modal.title")}
        open={open}
        handleSubmit={handleSubmit}
        bottomButtonSumbitText={t("pixel.modal.submit")}
        handleClose={() => setOpen(false)}
        bodyComponent={
          open ? (
            <div className="px-4 pt-4">
              <Input.Select
                value={provider}
                onInput={handleChangeProvider}
                label={t("pixel.modal.providerLabel")}
                placeholder={t("pixel.modal.providerPlaceholder")}
                options={providers
                  .filter((p) => p.identity !== "hosted")
                  .map((p) => ({
                    value: p.identity,
                    label: p.label,
                    icon: (
                      <img
                        src={p.icon}
                        alt={p.label}
                        className="select-pixel-provider-icon"
                      />
                    ),
                  }))}
                className="select-pixel-provider"
              />

              <Input
                placeholder=""
                autoFocus={true}
                value={id}
                onInput={setId}
                label={t("pixel.modal.idLabel")}
                className="mt-2"
              />

              {error && (
                <Material.Alert className="mt-2" severity="error">
                  {error}
                </Material.Alert>
              )}
            </div>
          ) : null
        }
      />
    </>
  );
}
